import React, { useState } from "react";
import { Download, FileText, FileSpreadsheet, Check } from "lucide-react";
import { toast } from "sonner";
import { gatewayApi } from "./api";

// "Download progress report": the whole school (principal) or the teacher's own classes, as a PDF or an Excel file.
// Same scopes as the Little Verses report: overall, daily, weekly, monthly. A checklist picks the applications: all of them in one
// combined report, or only the ticked ones (one file per app).
const SCOPES = [
  ["overall", "Overall", "Everyone, with their overall progress"],
  ["daily", "Daily", "Marks who was active in the last 24 hours"],
  ["weekly", "Weekly", "Marks who was active in the last 7 days"],
  ["monthly", "Monthly", "Marks who was active in the last 30 days"],
];

const save = (res, fallback) => {
  const cd = res.headers["content-disposition"] || "";
  const name = (cd.match(/filename="?([^"]+)"?/) || [])[1] || fallback;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

export default function SchoolReport({ token, role, apps = [] }) {
  const open = apps.filter((a) => a.has_access && a.available);
  const [scope, setScope] = useState("overall");
  const [all, setAll] = useState(true);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(null);

  const toggle = (id) => { setAll(false); setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])); };
  const chooseAll = () => { setAll(true); setPicked([]); };
  const targets = all ? [null] : open.filter((a) => picked.includes(a.id)).map((a) => a.id);

  const download = async (format) => {
    if (!targets.length) { toast.error("Tick at least one application."); return; }
    setBusy(format);
    let failed = 0;
    for (const product of targets) {
      try {
        const res = await gatewayApi.get("/school/report", {
          params: { period: scope, format, ...(product ? { product } : {}) }, responseType: "blob",
          headers: { Authorization: `Bearer ${token}` }, timeout: 120000,
        });
        save(res, `school-progress_${product || "all"}_${scope}.${format}`);
        if (targets.length > 1) await new Promise((r) => setTimeout(r, 400));   // browsers drop back-to-back downloads
      } catch { failed += 1; }
    }
    setBusy(null);
    if (failed) toast.error(failed === targets.length ? "Could not build the report. Please try again." : `${failed} report${failed === 1 ? "" : "s"} could not be built.`);
  };

  const btn = "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition disabled:opacity-60";
  const box = (on) => `flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold cursor-pointer ${on ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-sand-300 hover:bg-sand-100"}`;
  return (
    <section className="space-y-3" data-testid="school-report" id="school-report">
      <div className="sp-card" style={{ background: "#fff", border: "1px solid #E9E2CC", borderRadius: 16, padding: "18px 22px" }}>
        <h2 className="sp-h2" style={{ marginBottom: 4 }}><Download className="h-6 w-6" />Download progress report</h2>
        <p className="text-sm text-foreground/70">
          Choose the applications and the period, then download a PDF or an Excel file — {role === "principal" ? "every student and every teacher" : "every student in your classes"}.
        </p>

        <fieldset className="mt-4" data-testid="report-apps">
          <legend className="text-sm font-semibold mb-2">Applications</legend>
          <div className="flex flex-wrap gap-2">
            <label className={box(all)}><input type="checkbox" className="sr-only" checked={all} onChange={chooseAll} data-testid="report-app-all" />{all && <Check className="h-4 w-4" />} All applications (one combined report)</label>
            {open.map((a) => (
              <label key={a.id} className={box(!all && picked.includes(a.id))}>
                <input type="checkbox" className="sr-only" checked={!all && picked.includes(a.id)} onChange={() => toggle(a.id)} data-testid={`report-app-${a.id}`} />
                {!all && picked.includes(a.id) && <Check className="h-4 w-4" />}{a.name}</label>))}
          </div>
          {!all && picked.length > 1 && <p className="mt-2 text-xs text-foreground/60">One file per ticked application will be downloaded.</p>}
        </fieldset>

        <div className="mt-4 flex flex-wrap gap-2" role="tablist" data-testid="report-scope">
          {SCOPES.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={scope === k} data-testid={`report-scope-${k}`} onClick={() => setScope(k)}
              className={`rounded-full px-4 py-2 text-sm font-semibold border ${scope === k ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-sand-300 hover:bg-sand-100"}`}>{label}</button>))}
        </div>
        <p className="mt-2 text-xs text-foreground/60">{(SCOPES.find(([k]) => k === scope) || SCOPES[0])[2]}</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <button type="button" data-testid="report-download-pdf" disabled={!!busy} onClick={() => download("pdf")}
            className={`${btn} bg-forest-700 text-white hover:opacity-90`}>
            <FileText className="h-4 w-4" />{busy === "pdf" ? "Building PDF…" : "Download PDF"}</button>
          <button type="button" data-testid="report-download-xlsx" disabled={!!busy} onClick={() => download("xlsx")}
            className={`${btn} bg-white text-forest-700 border border-sand-300 hover:bg-sand-100`}>
            <FileSpreadsheet className="h-4 w-4" />{busy === "xlsx" ? "Building Excel…" : "Download Excel"}</button>
        </div>
        <p className="mt-3 text-xs text-foreground/60">Status, overall progress per app, last activity and whether each person was active in the period, as things stand right now.</p>
      </div>
    </section>
  );
}
